/**
 * Eine Zeile fuer eine Supplier-Invoice (Lieferanten-Rechnung) in Section „Rechnungen".
 *
 * Zeigt: Status-Pill, Lieferant, Kategorie, Betrag, Faelligkeit, Aktionsmenü.
 * „Bezahlt" setzt PaidOn=heute; den Status berechnet das Backend.
 */
import { useState } from 'react';
import { ConfirmDialog, Pill } from '@/shared/components';
import type { ActionMenuItem } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { formatCurrency } from '@/shared/lib/format';
import type { PropertyFileResponse, SupplierInvoiceResponse } from '@/shared/api';
import { AkteRowMenu } from './AkteRowMenu';
import { invoiceDueLabel, invoiceStatusDisplay } from './akteDomainPresenters';
import { AkteFileThumbnail } from './AkteFileThumbnail';

interface Props {
  invoice: SupplierInvoiceResponse;
  onDownload: (propertyFileId: string) => void;
  onPreview?: (propertyFileId: string) => void;
  onEdit?: (invoice: SupplierInvoiceResponse) => void;
  onMarkPaid: (invoice: SupplierInvoiceResponse) => void;
  onMarkDisputed: (invoice: SupplierInvoiceResponse) => void;
  onDelete: (invoice: SupplierInvoiceResponse) => void;
  propertyId?: string;
  displayMode?: 'list' | 'preview';
  file?: PropertyFileResponse | null;
}

export function SupplierInvoiceRow({
  invoice,
  onDownload,
  onPreview,
  onEdit,
  onMarkPaid,
  onMarkDisputed,
  onDelete,
  propertyId,
  displayMode = 'list',
  file,
}: Props) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const status = invoiceStatusDisplay(invoice);
  const due = invoiceDueLabel(invoice);
  const primary = onPreview ?? onDownload;
  const showThumb = displayMode === 'preview' && propertyId && file;

  const items: ActionMenuItem[] = [
    ...(onPreview
      ? [
          {
            label: 'Vorschau',
            icon: 'Eye' as const,
            onClick: () => onPreview(invoice.propertyFileId),
          },
        ]
      : []),
    { label: 'Herunterladen', icon: 'Download', onClick: () => onDownload(invoice.propertyFileId) },
    ...(onEdit
      ? [{ label: 'Bearbeiten', icon: 'Edit' as const, onClick: () => onEdit(invoice) }]
      : []),
    ...(invoice.status !== 'Paid'
      ? [
          {
            label: 'Als bezahlt markieren',
            icon: 'Check' as const,
            onClick: () => onMarkPaid(invoice),
          },
        ]
      : []),
    ...(invoice.status !== 'Disputed' && invoice.status !== 'Paid'
      ? [
          {
            label: 'Als strittig markieren',
            icon: 'Warning' as const,
            onClick: () => onMarkDisputed(invoice),
          },
        ]
      : []),
    { separator: true },
    {
      label: 'Löschen',
      icon: 'Trash',
      destructive: true,
      onClick: () => setConfirmingDelete(true),
    },
  ];

  return (
    <li
      className={cn(
        'hover:bg-card-alt/50 -mx-2 flex items-center gap-3 rounded px-2 text-sm transition-colors',
        showThumb ? 'py-2' : 'py-1',
      )}
    >
      {showThumb && <AkteFileThumbnail propertyId={propertyId} file={file} width={48} />}
      <button
        type="button"
        onClick={() => primary(invoice.propertyFileId)}
        className="flex min-h-11 min-w-0 flex-1 flex-col justify-center gap-1 text-left sm:flex-row sm:items-center sm:gap-3"
        title={onPreview ? 'Rechnung öffnen (Vorschau)' : 'Rechnung herunterladen'}
      >
        <span className="flex min-w-0 items-center gap-2">
          <Pill variant={status.variant} size="sm">
            {status.label}
          </Pill>
          <span className="text-ink min-w-0 truncate">
            <span className="font-medium">{invoice.supplier}</span>
            <span className="text-ink-soft"> · {invoice.category}</span>
          </span>
        </span>
        {/* Fälligkeit war unter sm ausgeblendet — bei einer offenen Rechnung ist sie der Grund,
            warum die Zeile überhaupt gelesen wird. */}
        <span className="text-ink-faint flex shrink-0 items-center gap-2 text-xs tabular-nums sm:ml-auto">
          <span>{due}</span>
          <span className="text-ink font-medium">
            · {formatCurrency(invoice.amount, invoice.currency)}
          </span>
        </span>
      </button>
      <AkteRowMenu items={items} label={`Aktionen für Rechnung ${invoice.supplier}`} />

      <ConfirmDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        title="Rechnung löschen?"
        description={`Die Rechnung von „${invoice.supplier}" wird samt Belegdatei aus der Akte entfernt. Das lässt sich nicht rückgängig machen.`}
        confirmLabel="Endgültig löschen"
        onConfirm={() => {
          setConfirmingDelete(false);
          onDelete(invoice);
        }}
      />
    </li>
  );
}
